import { Alert, Badge } from "@/shared/components/MDX/MDXComponents";

# 🔄 راهنمای مدیریت مسیرهای تولید (Routing)

پلتفرم کارخانه‌سازی میمزاد (Mimzad Manufacturing Platform) — سند راهنمای کاربری مسیرهای تولید (Routing)

<Badge type="gold">سند استاندارد کاربری</Badge> <Badge>راهنمای جامع</Badge>

## 📋 فهرست مطالب و دسترسی سریع

1. [مقدمه و مفاهیم مسیرهای تولید](#sec-intro)
2. [مدیریت خانواده مسیرها (Routing Families)](#sec-families)
3. [نسخه‌بندی مسیرها (Revisions)](#sec-revisions)
4. [تعریف عملیات‌ها و ایستگاه‌های کاری (Operations)](#sec-operations)
5. [مدیریت وابستگی‌ها و گراف عملیات (AND / OR)](#sec-dependencies)
6. [مدیریت دستورالعمل‌های کاری (SOP)](#sec-sops)

---

## 1. مقدمه و مفاهیم مسیرهای تولید (Routing) <a id="sec-intro"></a>

مسیر تولید (Routing) در سیستم میمزاد تعیین‌کننده توالی عملیات‌ها و ایستگاه‌های کاری است که قطعات و مواد خام برای تبدیل شدن به محصول نهایی باید طی کنند. این ماژول ارتباط مستقیمی با BOM (لیست مواد) دارد و تعیین می‌کند "چگونه" یک محصول باید ساخته شود.

<Alert type="info">
  **نکته کلیدی:** هر خانواده مسیر می‌تواند چندین نسخه (Revision) داشته باشد. در هر زمان تنها یک نسخه از هر خانواده می‌تواند در وضعیت **منتشرشده (Published)** باشد و برای تولید استفاده شود.
</Alert>

---

## 2. مدیریت خانواده مسیرها (Routing Families) <a id="sec-families"></a>

خانواده مسیر (Routing Family) یک گروه کلی برای مجموعه‌ای از مسیرهای تولیدی مرتبط به هم است. برای ثبت یک خانواده مسیر جدید:

1. در صفحه مسیرهای تولید، روی دکمه **«➕ خانواده جدید»** کلیک کنید.
2. **کد خانواده (Code):** شناسه منحصربه‌فرد برای این مسیر (مانند `RT-010`).
3. **نام خانواده (Name):** عنوان تشریحی برای مسیر (مانند `مسیر مونتاژ موتور`).
4. دکمه **«ذخیره»** را فشار دهید.

---

## 3. نسخه‌بندی مسیرها (Revisions) <a id="sec-revisions"></a>

هر تغییری در مسیرهای تولید باید در قالب یک نسخه (Revision) جدید ثبت شود تا سوابق تولید و قابلیت ردیابی حفظ گردد.

- **ایجاد نسخه جدید (Draft):** با انتخاب یک خانواده مسیر، از دکمه **«➕ نسخه جدید»** استفاده کنید و کد نسخه (مانند `v1.0` یا `RevA`) را وارد کنید. نسخه جدید همیشه در حالت **پیش‌نویس (Draft)** ایجاد می‌شود.
- **وضعیت نسخه‌ها:** نسخه‌ها در حالت پیش‌نویس (آبی رنگ) قابل ویرایش هستند. پس از تایید و اطمینان از صحت اطلاعات، با زدن دکمه **«انتشار (Publish)»**، وضعیت نسخه به **منتشرشده (سبز رنگ)** تغییر می‌کند و برای استفاده در سفارشات تولید آماده می‌شود. انتشار یک نسخه، به صورت خودکار نسخه منتشرشده قبلی (در صورت وجود) را منقضی (Archived) می‌کند.

<Alert type="warning">
  هنگامی که یک نسخه در وضعیت **منتشرشده (Published)** یا **بایگانی (Archived)** قرار می‌گیرد، دیگر امکان اضافه کردن، حذف یا ویرایش عملیات‌های آن وجود ندارد.
</Alert>

---

## 4. تعریف عملیات‌ها و ایستگاه‌های کاری (Operations) <a id="sec-operations"></a>

هر نسخه مسیر می‌تواند شامل چندین عملیات (Operation) باشد. برای افزودن عملیات:

1. روی دکمه **«⚙️ مدیریت ایستگاه‌ها و نسخه‌ها»** در ردیف مسیر مربوطه کلیک کنید تا پنجره جزئیات باز شود.
2. یک نسخه **پیش‌نویس (Draft)** را برای ویرایش انتخاب کنید.
3. در فرم افزودن ایستگاه، اطلاعات زیر را وارد نمایید:
   - **انتخاب ایستگاه کاری (Work Center):** محل فیزیکی انجام عملیات.
   - **شماره گام:** ترتیب این مرحله در کل مسیر (معمولا به صورت مضارب 10 وارد می‌شود).
   - **زمان آماده‌سازی (دقیقه):** زمان راه‌اندازی و تنظیم تجهیزات.
   - **زمان ساخت (دقیقه):** زمان خالص مورد نیاز برای پردازش محصول.
4. روی دکمه **«+ افزودن ایستگاه به این نسخه»** کلیک کنید تا ایستگاه در جدول ثبت شود.

---

## 5. مدیریت وابستگی‌ها و گراف عملیات (AND / OR) <a id="sec-dependencies"></a>

در بسیاری از فرایندهای تولید پیچیده، مراحل به صورت یک خط مستقیم نیستند و برخی عملیات‌ها نیازمند پایان یافتن چند مرحله قبلی به صورت همزمان هستند. 

در پایین فهرست ایستگاه‌ها، بخش **وابستگی‌های عملیات** قرار دارد:

- **نمای گرافیکی:** سیستم به صورت خودکار یک گراف (فلوچارت) از ترتیب و ارتباطات ایستگاه‌های کاری شما رسم می‌کند.
- **افزودن وابستگی (فلش):** با انتخاب «عملیات مبدأ» و «عملیات مقصد» در فرم پایین و زدن دکمه **افزودن وابستگی**، به سیستم اعلام می‌کنید که انجام عملیات مقصد، نیازمند پایان یافتن عملیات مبدأ است (و بلافاصله یک فلش در گراف رسم می‌شود).
- **منطق ادغام (AND / OR):** در صورتی که یک ایستگاهِ مقصد دارای بیش از یک ایستگاه پیش‌نیاز (مبدأ) باشد، در جدول امکان انتخاب شرط ادغام را خواهید داشت:
   - **AND (همه لازم):** عملیات مقصد تنها زمانی آغاز می‌شود که **همه** پیش‌نیازهای آن با موفقیت تمام شده باشند (شرط پیش‌فرض).
   - **OR (هرکدام کافی):** عملیات مقصد به محض تمام شدن **حداقل یکی** از پیش‌نیازها می‌تواند آغاز گردد (کلمه OR روی فلش‌های ورودی گراف درج خواهد شد).

<Alert type="tip">
  شما می‌توانید عملیات‌ها و وابستگی‌ها را به هر شکل که مسیر تولید شما می‌طلبد طراحی کنید. پس از پایان تنظیمات، با فشردن دکمه **«🚀 انتشار نسخه»** این مسیر برای ارجاع به سفارش‌های تولید نهایی می‌گردد.
</Alert>

---

## 6. مدیریت دستورالعمل‌های کاری (SOP) <a id="sec-sops"></a>

برای استانداردسازی فرایند تولید، می‌توانید برای هر گام عملیاتی در مسیر، دستورالعمل‌های کاری (SOP) یا استانداردهای عملیاتی تعریف کنید. این دستورالعمل‌ها شامل متن، هشدارها و نکات ایمنی هستند که اپراتورها در حین کار در خط تولید (Shopfloor) مشاهده می‌کنند.

برای ایجاد دستورالعمل:

1. در جدول گام‌های عملیاتی (عملیات‌ها)، روی دکمه **«📋 دستورالعمل‌ها»** در مقابل ایستگاه مورد نظر کلیک کنید تا پنجرهٔ مدیریت SOP باز شود.
2. برای تعریف یک خانواده دستورالعمل جدید، در کادر پایینِ لیست، کد دلخواه خود (مثلاً `SOP-ASSY-01`) را وارد کرده و دکمه **«افزودن SOP»** را بزنید.
3. در لیست، روبروی دستورالعملی که ایجاد کردید، دکمهٔ **«ویرایش دستورالعمل»** را بزنید.
4. در ویرایشگری که باز می‌شود، محتوای آموزشی، نکات فنی و ایمنی مربوط به آن ایستگاه را نوشته و سپس روی دکمه **«انتشار نسخه جدید»** کلیک کنید.
5. این دستورالعملِ منتشر شده، بلافاصله در پنل اپراتورها در داشبورد خط تولید، به عنوان راهنمای همان ایستگاه قابل مشاهده و استفاده خواهد بود.
